import { NEW_BEST_RING_S } from '../audio/sfx';
import { mwLabel } from '../game/scoring';
import { must, replayAnimation, setVisible } from './dom';
import { str } from './strings';

/** A count drawn past this stops reading at a glance and falls back to text. */
const GLYPHS_MAX = 8;

/**
 * Draw a count as the things themselves (STYLEGUIDE §7): `total` slots of
 * the map's own pictogram, the first `filled` of them filled. The row is
 * decoration over the numeral beside it — which stays sr-only text while
 * the row draws, and becomes the visible value when it cannot (no limit,
 * or a count past reading at a glance).
 */
function drawCount(rowId: string, countId: string, symbol: string, total: number, filled: number) {
  const numeral = must(countId);
  const drawable = Number.isFinite(total) && total > 0 && total <= GLYPHS_MAX;
  numeral.classList.toggle('sr-only', drawable);
  must(rowId).innerHTML = drawable
    ? Array.from(
        { length: total },
        (_, i) =>
          `<svg class="count-glyph${i < filled ? ' filled' : ''}"><use href="#${symbol}"/></svg>`,
      ).join('')
    : '';
}

/** The operator readout across the top of the board. */
export const hud = {
  setTowers(placed: number, par: number): void {
    // The slash sits tight against its numbers: mono spaces its own count,
    // and a padded "4 / 4" reads looser than the readout it belongs to.
    must('towerCount').textContent = Number.isFinite(par) ? `${placed}/${par}` : `${placed}`;
    drawCount('towerGlyphs', 'towerCount', 'pylonGlyph', par, placed);
  },

  setHouses(lit: number, total: number): void {
    must('houseCount').textContent = `${lit}/${total}`;
    drawCount('houseGlyphs', 'houseCount', 'iconHouse', total, lit);
  },

  setMegawatts(mw: number | null): void {
    must('mwCount').textContent = mw === null ? str().darkDash : mwLabel(mw);
    // delivered output is live circuit — the readout's one amber value
    must('mwChip').classList.toggle('live', mw !== null);
  },

  setStreak(days: number | null): void {
    const chip = must('streakChip');
    // A number, zero included, is a fixture; `null` means the mode has no
    // streak business at all (STYLEGUIDE §7, second edition).
    setVisible(chip, days !== null);
    if (days !== null) must('streakCount').textContent = String(days);
  },

  /** Shake the tower readout when the player is out of towers. */
  flashTowerLimit(): void {
    replayAnimation(must('towerChip'), 'shake');
  },

  /**
   * Flash the delivered-MW readout when a new best lands — for exactly as
   * long as the `newBest` cue rings, published as --best-flash (one clock).
   */
  flashNewBest(): void {
    const chip = must('mwChip');
    chip.style.setProperty('--best-flash', `${NEW_BEST_RING_S}s`);
    replayAnimation(chip, 'best-flash');
  },
};
